import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Collapse,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  People as PeopleIcon,
  Info as InfoIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import PropTypes from 'prop-types';

const MultiCustomerDuplicates = ({
  data,
  loading,
  pagination,
  onSearch,
  onView,
  onViewLead,
  onSend,
  onRefresh,
  onPageChange,
  onPageSizeChange,
  onOpenLeadHistory,
}) => {
  const [expandedRows, setExpandedRows] = React.useState(new Set());
  const [searchTerm, setSearchTerm] = React.useState('');
  const [openOtherDataModal, setOpenOtherDataModal] = React.useState(false);
  const [selectedOtherData, setSelectedOtherData] = React.useState(null);
  const searchDebounceRef = React.useRef(null);

  // Safely get customer count for a row
  const getCustomerCount = (row) => {
    if (typeof row.customer_count === 'number') return row.customer_count;
    return row.customers ? row.customers.length : 0;
  };

  // Get the most recent sent_at timestamp among customers for a row
  const getMaxSentAt = (row) => {
    const customers = row.customers || [];
    if (!customers || customers.length === 0) return null;
    const maxTs = customers
      .map((c) => (c && c.sent_at ? new Date(c.sent_at).getTime() : 0))
      .reduce((a, b) => Math.max(a, b), 0);
    return maxTs > 0 ? new Date(maxTs) : null;
  };

  // Highlight when lead re-entered the system (duplicate re-upload)
  // Prefer backend flag `has_recent_duplicate` when present; otherwise fallback to heuristics
  const shouldHighlightRow = (row) => {
    if (
      row.has_recent_duplicate !== undefined &&
      row.has_recent_duplicate !== null
    ) {
      return Number(row.has_recent_duplicate) === 1;
    }
    const customersCount = getCustomerCount(row);
    if (customersCount <= 1) return false;
    if (!row.created_at) return false;
    const createdAt = new Date(row.created_at);
    const maxSentAt = getMaxSentAt(row);
    const reEnteredAfterSent = maxSentAt
      ? createdAt.getTime() > maxSentAt.getTime()
      : false;
    const RECENT_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours
    const isVeryRecent = Date.now() - createdAt.getTime() <= RECENT_WINDOW_MS;
    return reEnteredAfterSent || isVeryRecent;
  };

  const handleRowExpand = (rowId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      if (onSearch) onSearch(value);
    }, 300);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'api':
        return 'primary';
      case 'file_upload':
        return 'secondary';
      case 'manual':
        return 'success';
      default:
        return 'default';
    }
  };

  // Skeleton loading component
  const SkeletonRow = () => (
    <TableRow hover>
      {/* Lead Information */}
      <TableCell>
        <Box>
          <Skeleton
            variant='text'
            width='60%'
            height={20}
            sx={{fontWeight: 'bold'}}
          />
          <Skeleton variant='text' width='80%' height={16} sx={{mt: 0.5}} />
          <Skeleton variant='text' width='70%' height={16} sx={{mt: 0.5}} />
          <Skeleton variant='text' width='50%' height={16} sx={{mt: 0.5}} />
          <Box mt={1}>
            <Skeleton
              variant='rectangular'
              width={100}
              height={24}
              sx={{borderRadius: 1}}
            />
          </Box>
        </Box>
      </TableCell>

      {/* Source */}
      <TableCell>
        <Box display='flex' alignItems='center' gap={1}>
          <Skeleton
            variant='rectangular'
            width={80}
            height={24}
            sx={{borderRadius: 1}}
          />
        </Box>
      </TableCell>

      {/* Integration Source */}
      <TableCell>
        <Skeleton
          variant='rectangular'
          width={100}
          height={24}
          sx={{borderRadius: 1}}
        />
      </TableCell>

      {/* Other Data */}
      <TableCell>
        <Skeleton
          variant='rectangular'
          width={60}
          height={24}
          sx={{borderRadius: 1}}
        />
      </TableCell>

      {/* Sent To */}
      <TableCell>
        <Box display='flex' alignItems='center' gap={1}>
          <Box>
            <Skeleton variant='text' width='60%' height={16} sx={{mb: 1}} />
            <Box display='flex' flexWrap='wrap' gap={0.5}>
              <Skeleton
                variant='rectangular'
                width={60}
                height={24}
                sx={{borderRadius: 1}}
              />
              <Skeleton
                variant='rectangular'
                width={50}
                height={24}
                sx={{borderRadius: 1}}
              />
              <Skeleton
                variant='rectangular'
                width={70}
                height={24}
                sx={{borderRadius: 1}}
              />
            </Box>
          </Box>
          <Skeleton variant='circular' width={24} height={24} />
        </Box>
      </TableCell>

      {/* Created Date */}
      <TableCell>
        <Skeleton variant='text' width='80%' height={16} />
      </TableCell>

      {/* Actions */}
      <TableCell>
        <Box display='flex' gap={0.5}>
          <Skeleton variant='circular' width={32} height={32} />
          <Skeleton variant='circular' width={32} height={32} />
          <Skeleton variant='circular' width={32} height={32} />
        </Box>
      </TableCell>
    </TableRow>
  );

  // Loading handled in-table; keep search fields visible

  return (
    <Box>
      {/* Search and Filter Bar */}
      <Card sx={{mb: 3}}>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder='Search by name, email, phone, marks, MPC_4, or other data content...'
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size='small'
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{textAlign: 'right'}}>
              <Button
                variant='outlined'
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                size='small'
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Multi-Customer Duplicates Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                >
                  Lead Info
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                >
                  Source
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                >
                  Integration Source
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                >
                  Other Data
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                >
                  Sent To
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                >
                  Created
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                >
                  Last Sent
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({length: pagination?.limit || 50}).map(
                  (_, index) => <SkeletonRow key={index} />,
                )
              ) : !data || data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align='center'>
                    <Typography variant='body2' color='text.secondary'>
                      No data found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                [...data]
                  .sort((a, b) => {
                    // Sort: highlighted first, then latest last_sent_at, then newest created
                    const aHL = shouldHighlightRow(a) ? 1 : 0;
                    const bHL = shouldHighlightRow(b) ? 1 : 0;
                    if (bHL !== aHL) return bHL - aHL;
                    const aLast = a.last_sent_at
                      ? new Date(a.last_sent_at).getTime()
                      : getMaxSentAt(a)?.getTime() || 0;
                    const bLast = b.last_sent_at
                      ? new Date(b.last_sent_at).getTime()
                      : getMaxSentAt(b)?.getTime() || 0;
                    if (bLast !== aLast) return bLast - aLast;
                    const aDate = a.created_at
                      ? new Date(a.created_at).getTime()
                      : 0;
                    const bDate = b.created_at
                      ? new Date(b.created_at).getTime()
                      : 0;
                    return bDate - aDate;
                  })
                  .map((row) => (
                    <React.Fragment key={row.id}>
                      <TableRow
                        hover
                        sx={{
                          backgroundColor: shouldHighlightRow(row)
                            ? '#fff3e0'
                            : 'inherit',
                          '&:hover': {
                            backgroundColor: shouldHighlightRow(row)
                              ? '#ffe0b2'
                              : 'inherit',
                          },
                        }}
                      >
                        {/* Lead Information */}
                        <TableCell>
                          <Box>
                            <Typography
                              variant='body2'
                              fontWeight='bold'
                              color='primary'
                            >
                              {row.fullName}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              display='block'
                            >
                              📧 {row.email}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              display='block'
                            >
                              📞 {row.phone}
                            </Typography>
                            {row.country && (
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                display='block'
                              >
                                🌍 {row.country}
                              </Typography>
                            )}
                            {row.MPC_4 && (
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                display='block'
                              >
                                📋 MPC_4: {row.MPC_4}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        {/* Source */}
                        <TableCell>
                          <Box display='flex' alignItems='center' gap={1}>
                            <Chip
                              label={row.data_source || 'Unknown'}
                              color={getSourceColor(row.data_source)}
                              size='small'
                              variant='outlined'
                            />
                          </Box>
                        </TableCell>

                        {/* Integration Source */}
                        <TableCell>
                          {row.integration_source ? (
                            <Chip
                              label={row.integration_source}
                              color='info'
                              size='small'
                              variant='outlined'
                            />
                          ) : (
                            <Typography variant='body2' color='text.secondary'>
                              N/A
                            </Typography>
                          )}
                        </TableCell>

                        {/* Other Data */}
                        <TableCell>
                          {row.other_data ? (
                            <Button
                              size='small'
                              variant='outlined'
                              onClick={() => {
                                // If row.other_data is an object with OTHER_DATA field, extract it
                                // Otherwise, use the value directly
                                const otherDataValue =
                                  row.other_data &&
                                  typeof row.other_data === 'object' &&
                                  row.other_data.OTHER_DATA
                                    ? row.other_data.OTHER_DATA
                                    : row.other_data;
                                setSelectedOtherData(otherDataValue);
                                setOpenOtherDataModal(true);
                              }}
                            >
                              Show
                            </Button>
                          ) : (
                            <Typography variant='body2' color='text.secondary'>
                              N/A
                            </Typography>
                          )}
                        </TableCell>

                        {/* Sent To */}
                        <TableCell>
                          <Box display='flex' alignItems='center' gap={1}>
                            <Box>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                                gutterBottom
                              >
                                {getCustomerCount(row)} customer(s)
                              </Typography>
                              <Box display='flex' flexWrap='wrap' gap={0.5}>
                                {(row.customers || [])
                                  .slice(0, 2)
                                  .map((customer, index) => (
                                    <Chip
                                      key={index}
                                      label={
                                        customer.customer_name ||
                                        `Customer ${customer.customer_id}`
                                      }
                                      size='small'
                                      color='primary'
                                      variant='outlined'
                                    />
                                  ))}
                                {(row.customers || []).length > 2 && (
                                  <Chip
                                    label={`+${
                                      (row.customers || []).length - 2
                                    } more`}
                                    size='small'
                                    color='default'
                                    variant='outlined'
                                  />
                                )}
                              </Box>
                            </Box>
                            {(row.customers || []).length > 0 && (
                              <IconButton
                                size='small'
                                onClick={() => handleRowExpand(row.id)}
                              >
                                {expandedRows.has(row.id) ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>

                        {/* Created Date */}
                        <TableCell>
                          <Typography variant='body2'>
                            {formatDate(row.created_at)}
                          </Typography>
                        </TableCell>

                        {/* Last Sent Date */}
                        <TableCell>
                          <Typography variant='body2'>
                            {formatDate(
                              row.last_sent_at ||
                                (getMaxSentAt(row)
                                  ? getMaxSentAt(row).toISOString()
                                  : null),
                            )}
                          </Typography>
                        </TableCell>

                        {/* Actions */}
                        <TableCell>
                          <Box display='flex' gap={0.5} alignItems='center'>
                            {(() => {
                              const count =
                                Number(row.lead_history_count || 0) || 0;
                              const title =
                                count > 0
                                  ? `${count} failure${count === 1 ? '' : 's'}`
                                  : 'No failures';
                              return (
                                <Tooltip title={title}>
                                  <span>
                                    {count > 0 && (
                                      <Chip
                                        label={`×${count}`}
                                        size='small'
                                        color='warning'
                                        variant='outlined'
                                        sx={{height: '22px'}}
                                      />
                                    )}
                                    <IconButton
                                      size='small'
                                      color={count > 0 ? 'error' : 'default'}
                                      onClick={() =>
                                        onOpenLeadHistory &&
                                        onOpenLeadHistory(row)
                                      }
                                      sx={{ml: count > 0 ? 0.5 : 0}}
                                    >
                                      <InfoOutlinedIcon fontSize='small' />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              );
                            })()}

                            <Tooltip title='View Customer Details'>
                              <IconButton
                                size='small'
                                color='primary'
                                onClick={() =>
                                  onView && onView(row.customers || [])
                                }
                              >
                                <InfoIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title='View Lead'>
                              <IconButton
                                size='small'
                                onClick={() => onViewLead && onViewLead(row)}
                              >
                                <ViewIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title='Send to Customer'>
                              <IconButton
                                size='small'
                                color='success'
                                onClick={() => onSend && onSend(row)}
                              >
                                <SendIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Row - Customer Details */}
                      <TableRow>
                        <TableCell colSpan={6} sx={{py: 0}}>
                          <Collapse
                            in={expandedRows.has(row.id)}
                            timeout='auto'
                            unmountOnExit
                          >
                            <Box sx={{p: 2, backgroundColor: 'grey.50'}}>
                              <Typography variant='subtitle2' gutterBottom>
                                Customer Distribution Details
                              </Typography>
                              <Grid container spacing={2}>
                                {(row.customers || []).map(
                                  (customer, index) => (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={4}
                                      key={index}
                                    >
                                      <Card variant='outlined' sx={{p: 1}}>
                                        <Box
                                          display='flex'
                                          alignItems='center'
                                          mb={1}
                                        >
                                          <PeopleIcon
                                            sx={{mr: 1, color: 'primary.main'}}
                                          />
                                          <Typography
                                            variant='body2'
                                            fontWeight='bold'
                                          >
                                            {customer.customer_name ||
                                              `Customer ${customer.customer_id}`}
                                          </Typography>
                                        </Box>
                                        <Typography
                                          variant='caption'
                                          color='text.secondary'
                                          display='block'
                                        >
                                          Customer ID: {customer.customer_id}
                                        </Typography>
                                        <Typography
                                          variant='caption'
                                          color='text.secondary'
                                          display='block'
                                        >
                                          Sent: {formatDate(customer.sent_at)}
                                        </Typography>
                                      </Card>
                                    </Grid>
                                  ),
                                )}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination Controls */}
      {pagination && (
        <Card sx={{mt: 3}}>
          <CardContent>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              flexWrap='wrap'
              gap={2}
            >
              {/* Page Size Selector */}
              <Box display='flex' alignItems='center' gap={2}>
                <Typography variant='body2' color='text.secondary'>
                  Rows per page:
                </Typography>
                <FormControl size='small' sx={{minWidth: 80}}>
                  <Select
                    value={pagination.limit || 50}
                    onChange={(e) =>
                      onPageSizeChange && onPageSizeChange(e.target.value)
                    }
                    displayEmpty
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Pagination Info */}
              <Typography variant='body2' color='text.secondary'>
                Showing{' '}
                {pagination.total
                  ? (pagination.page - 1) * pagination.limit + 1
                  : 0}{' '}
                to{' '}
                {Math.min(
                  pagination.page * pagination.limit,
                  pagination.total || 0,
                )}{' '}
                of {pagination.total || 0} entries
              </Typography>

              {/* Pagination Component */}
              <Pagination
                count={pagination.totalPages || 0}
                page={pagination.page || 1}
                onChange={(event, page) => onPageChange && onPageChange(page)}
                color='primary'
                showFirstButton
                showLastButton
                size='large'
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Other Data Modal */}
      <Dialog
        open={openOtherDataModal}
        onClose={() => {
          setOpenOtherDataModal(false);
          setSelectedOtherData(null);
        }}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={1}>
            <Typography variant='h6'>Other Data</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{mt: 2}}>
            {selectedOtherData ? (
              <TextField
                fullWidth
                multiline
                rows={15}
                value={
                  typeof selectedOtherData === 'object'
                    ? JSON.stringify(selectedOtherData, null, 2)
                    : String(selectedOtherData)
                }
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  },
                }}
              />
            ) : (
              <Typography variant='body2' color='text.secondary'>
                No other data available
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenOtherDataModal(false);
              setSelectedOtherData(null);
            }}
            variant='contained'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

MultiCustomerDuplicates.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  pagination: PropTypes.object,
  onSearch: PropTypes.func,
  onView: PropTypes.func,
  onViewLead: PropTypes.func,
  onSend: PropTypes.func,
  onRefresh: PropTypes.func,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onOpenLeadHistory: PropTypes.func,
};

export default MultiCustomerDuplicates;
